import { api } from '@/api'
import { putForUpload } from '@/api/core'
import SparkMD5 from 'spark-md5'


export const SUPPORTED_VIDEO_EXT = ['mp4', 'mov', 'quicktime']

class UploadService {

  /** 分片上传 */
  public async sliceUploadVideoFile(param: { file: File; module: Models.FileUploadModule }) {
    const { file, module } = param
    const md5 = await this.genFileMD5(file)
    try {
      // 初始化上传，获取fileId，并检查文件是否已存在
      const { fileId, hasUpload } = await api.upload.getSliceUploadVideoSignUrl({
        md5,
        module,
        filename: this.processFileNameExt(file.name),
        contentLength: file.size,
        partNumber: 1
      })
      // 2. 如果文件未上传，则执行分片上传
      if (!hasUpload) {
        const chunkSize = 5 * 1024 * 1024 // 5MB per chunk
        const chunkCount = Math.ceil(file.size / chunkSize)
        let chunks: Array<{ chunk: Blob, partNumber: number }> = []
        for (let i = 0; i < chunkCount; i++) {
          const partNumber = i + 1
          const start = i * chunkSize
          const end = Math.min(start + chunkSize, file.size)
          const chunk = file.slice(start, end)
          chunks = [...chunks, { chunk, partNumber }]
        }
        const uploadTasks = chunks.map((chunkData) => {
          return async () => {
            const { signUrl } = await api.upload.getSliceUploadVideoSignUrl({
              md5,
              module,
              filename: this.processFileNameExt(file.name),
              contentLength: file.size,
              partNumber: chunkData.partNumber,
            })
            await putForUpload(signUrl, chunkData.chunk).send()
          }
        })

        const concurrencyLimit = 2
        for (let i = 0; i < uploadTasks.length; i += concurrencyLimit) {
          const batch = uploadTasks.slice(i, i + concurrencyLimit)
          await Promise.all(batch.map((task) => task()))
        }

        // for (const chunkData of chunks) {
        //   // 为每个分片获取预签名URL并创建上传任务
        //   const res = await api.upload
        //     .getSliceUploadVideoSignUrl({
        //       md5,
        //       module,
        //       filename: file.name,
        //       contentLength: file.size,
        //       partNumber: chunkData.partNumber,
        //     })
        //   const { signUrl } = res
        //   await putForUpload(signUrl, chunkData.chunk).send()
        // }
        await api.upload.sliceVideoUploadedCheck({ fileId })
      }
      const { downloadUrl } = await api.upload.uploadVideoDownloadUrl({ fileId })
      return { error: '', downloadUrl, fileId }
    } catch (error) {
      console.error('Slice upload failed:', error)
      return { error: '上传失败', downloadUrl: '', fileId: 0 }
    }
  }

  public async uploadVideoFile(param: { file: File; module: Models.FileUploadModule }) {
    try {
      const { file, module } = param
      const md5 = await this.genFileMD5(file)
      const result = await api.upload.getUploadVideoSignUrl({
        md5,
        module,
        filename: this.processFileNameExt(file.name),
        contentLength: file.size,
      })
      const { signUrl, fileId, hasUpload } = result
      if (!hasUpload) {
        const blob = new Blob([file], { type: file.type })
        await putForUpload(signUrl, blob).send()
        await api.upload.uploadVideoStatus({ fileId })
      }
      const { downloadUrl } = await api.upload.uploadVideoDownloadUrl({ fileId })
      return { error: '', downloadUrl, fileId }
    } catch (error) {
      console.error(error)
      return { error: '上传失败', downloadUrl: '', fileId: 0 }
    }
  }

  public genFileMD5(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const blobSlice = File.prototype.slice
      const chunkSize = 2097152
      const chunks = Math.ceil(file.size / chunkSize)
      let currentChunk = 0

      const spark = new SparkMD5.ArrayBuffer()
      const fr = new FileReader()

      const doFileRead = () => {
        const start = currentChunk * chunkSize
        const end = start + chunkSize >= file.size ? file.size : start + chunkSize
        fr.readAsArrayBuffer(blobSlice.call(file, start, end))
      }

      fr.onload = (e: any) => {
        spark.append(e.target.result as ArrayBuffer)
        currentChunk++
        if (currentChunk < chunks) {
          doFileRead()
        } else {
          const hash = spark.end()
          resolve(hash)
        }
      }

      fr.onerror = () => {
        console.warn('oops, something went wrong.')
        reject()
      }

      doFileRead()
    })
  }

  private processFileNameExt(fileName: string) {
    const upperCaseExts = SUPPORTED_VIDEO_EXT.map((ext) => ext.toUpperCase())
    const exts = fileName.split('.')
    if (exts.length > 1) {
      const ext = exts[exts.length - 1]
      if (upperCaseExts.includes(ext.toUpperCase())) {
        const rest = exts.slice(0, exts.length - 1)
        return `${rest}.${ext.toLowerCase()}`
      }
    }
    return fileName
  }
}

export const uploadService = new UploadService()
